import { getAdobeAccessToken } from '../auth/adobeIms';
import { resolveRegion } from './regionConfig';
import { daysBetween } from './date';
import type { Deal } from '../types';

function quarterFromDate(dateStr: string): { fy: string; q: string } {
  if (!dateStr) return { fy: '', q: '' };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { fy: '', q: '' };
  const month = d.getMonth() + 1;
  const q = month <= 3 ? 'Q1' : month <= 6 ? 'Q2' : month <= 9 ? 'Q3' : 'Q4';
  return { fy: String(d.getFullYear()), q };
}

export function normalizeRawRecord(raw: Record<string, unknown>): Deal {
  const entry = String(raw.entry ?? '');
  const completion = String(raw.completion ?? '');
  const { fy, q } = quarterFromDate(entry);
  const market_area = raw.market_area !== undefined ? String(raw.market_area) : undefined;
  const segmentForSubRegion = raw.segment_sales ?? raw.segment;
  const segment_sales = raw.segment_sales !== undefined ? String(raw.segment_sales) : undefined;
  const resolved = resolveRegion(market_area, segmentForSubRegion !== undefined ? String(segmentForSubRegion) : undefined);
  return {
    id: String(raw.id ?? ''),
    account: String(raw.account ?? ''),
    sc: String(raw.sc ?? ''),
    status: String(raw.status ?? ''),
    status_eq: String(raw.status_eq ?? raw.status ?? ''),
    age: daysBetween(entry, completion),
    gnarr: parseFloat(String(raw.gnarr ?? '0')) || 0,
    region: resolved?.region ?? String(raw.region ?? ''),
    sub_region: resolved?.sub_region ?? String(raw.sub_region ?? raw.subregion ?? ''),
    close_q: String(raw.close_q ?? ''),
    offering: String(raw.offering ?? ''),
    requestName: String(raw.requestName ?? ''),
    requestID: String(raw.requestID ?? ''),
    products: String(raw.products ?? ''),
    entry,
    completion,
    sales_region: String(raw.sales_region ?? ''),
    adjusted_commit: String(raw.adjusted_commit ?? ''),
    opp_gnarr: parseFloat(String(raw.opp_gnarr ?? '0')) || 0,
    created_q: String(raw.created_q ?? q),
    created_fy: String(raw.created_fy ?? fy),
    raw_sub: raw.raw_sub !== undefined ? String(raw.raw_sub) : undefined,
    segment: raw.segment !== undefined ? String(raw.segment) : undefined,
    segment_sales,
    market_area,
  };
}

const DB_ACTION_BASE_URL = 'https://55338-809mauveporcupine-stage.adobeioruntime.net/api/v1/web/ondemand-dashboard';

const REPORT_ID = 'all-ondemand-global-requests';

export interface UserRecord {
  email: string;
  name: string | null;
  accessLevel: AdobeImsAccessLevel;
}

export interface ReportData {
  records: Deal[];
  count: number;
  lastSyncedAt: string | null;
  fromCache?: boolean;
  stale?: boolean;
  syncError?: string;
}

export interface SyncResult {
  records: Deal[];
  count: number;
  lastSyncedAt: string;
  syncCounts?: {
    teams: number;
    users: number;
    assignments: number;
    tasks: number;
    issues: number;
    projects: number;
  };
  stale?: boolean;
  syncError?: string;
}

function actionUrl(action: string): string {
  return `${DB_ACTION_BASE_URL.replace(/\/$/, '')}/${action}`;
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchUserRecord(userEmail: string): Promise<UserRecord | null> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-get-user'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ email: userEmail }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.found) return null;
    return data.user as UserRecord;
  } catch (error) {
    console.error('fetchUserRecord error:', error);
    return null;
  }
}

export async function fetchDeals(reportId = REPORT_ID): Promise<ReportData | null> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('get-report-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!response.ok) {
      console.error(`fetchDeals HTTP ${response.status}`);
      return null;
    }
    const raw = await response.json();
    return {
      records: Array.isArray(raw.data) ? raw.data.map((r: Record<string, unknown>) => normalizeRawRecord(r)) : [],
      count: raw.count || 0,
      lastSyncedAt: raw.lastSyncedAt || null,
      fromCache: raw.fromCache,
      stale: raw.stale,
      syncError: raw.syncError,
    };
  } catch (error) {
    console.error('fetchDeals error:', error);
    return null;
  }
}

const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function triggerSync(reportId = REPORT_ID): Promise<SyncResult | null> {
  try {
    const token = getAdobeAccessToken();

    // 1. Fire async worker — returns activationId immediately
    const startResp = await fetch(actionUrl('sync-report-trigger'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!startResp.ok) {
      const errBody = await startResp.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${startResp.status}`);
    }
    const startData = await startResp.json();
    const activationId: string = startData.activationId;
    if (!activationId) {
      throw new Error('sync-report-trigger did not return an activationId');
    }

    // 2. Poll until complete, error, or timeout
    const deadline = Date.now() + SYNC_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise<void>(r => setTimeout(r, SYNC_POLL_INTERVAL_MS));

      const pollResp = await fetch(actionUrl('check-sync-status'), {
        method: 'POST',
        headers: authHeaders(token || ''),
        body: JSON.stringify({ activationId }),
      });
      if (!pollResp.ok) {
        console.warn(`triggerSync: poll returned ${pollResp.status}, retrying`);
        continue;
      }

      const poll = await pollResp.json();
      if (poll.status === 'complete') {
        return {
          records: Array.isArray(poll.data)
            ? poll.data.map((r: Record<string, unknown>) => normalizeRawRecord(r))
            : [],
          count: poll.count || 0,
          lastSyncedAt: poll.lastSyncedAt || new Date().toISOString(),
          syncCounts: poll.syncCounts,
          stale: false,
        };
      }
      if (poll.status === 'error') {
        throw new Error(poll.error || 'Sync failed');
      }
      // status === 'running' — keep polling
    }

    throw new Error('Sync timed out — the job is still running in the background');
  } catch (error) {
    console.error('triggerSync error:', error);
    throw error;
  }
}

export interface EligibleCountsResult {
  counts: Record<string, unknown>;
  heatmapDenominator: Record<string, number>;
  reportId: string;
  errorCount: number;
  lastSyncedAt?: string;
}

export interface CachedDenominatorsResult {
  heatmapDenominator: Record<string, number>;
  lastSyncedAt: string | null;
}

export async function fetchCachedDenominators(reportId: string): Promise<CachedDenominatorsResult | null> {
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('get-report-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!resp.ok) return null;
    const body = await resp.json();
    if (!body?.heatmapDenominator) return null;
    return {
      heatmapDenominator: body.heatmapDenominator as Record<string, number>,
      lastSyncedAt: body.lastSyncedAt ?? null,
    };
  } catch (error) {
    console.error('fetchCachedDenominators error:', error);
    return null;
  }
}

export async function fetchEligibleCounts(reportId: string): Promise<EligibleCountsResult | null> {
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('fetch-eligible-counts'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ reportId }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'unknown' }));
      throw new Error((err as { error?: string }).error || `HTTP ${resp.status}`);
    }
    return await resp.json();
  } catch (error) {
    console.error('fetchEligibleCounts error:', error);
    return null;
  }
}

export async function manageUsers(
  action: 'list' | 'upsert' | 'remove',
  payload?: { email?: string; name?: string; accessLevel?: string }
): Promise<unknown> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('db-manage-users'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ action, ...payload }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('manageUsers error:', error);
    throw error;
  }
}

export type UploadUntrackedMode = 'append' | 'replace';

export interface UploadUntrackedResult {
  mode: UploadUntrackedMode;
  inserted: number;
  skippedCount: number;
  skipped: string[];
}

export async function uploadUntrackedCsv(
  rows: Record<string, string>[],
  mode: UploadUntrackedMode = 'append'
): Promise<UploadUntrackedResult> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-upload-untracked-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({ rows, mode }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('uploadUntrackedCsv error:', error);
    throw error;
  }
}

export async function fetchUntrackedData(): Promise<Record<string, string>[]> {
  try {
    const token = getAdobeAccessToken();
    const response = await fetch(actionUrl('admin-export-untracked-data'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({ error: 'unknown' }));
      throw new Error(errBody.error || `HTTP ${response.status}`);
    }
    const body = await response.json();
    return Array.isArray(body.rows) ? body.rows : [];
  } catch (error) {
    console.error('fetchUntrackedData error:', error);
    throw error;
  }
}

export interface ImsGroup {
  id: string | null;
  name: string | null;
  rawGroupName: string | null;
  role: string | null;
  groupType: string | null;
}

export interface ImsOrg {
  imsOrgId: string;
  orgName: string | null;
  orgType: string | null;
  countryCode: string | null;
  groups: ImsGroup[];
  sourceUserIds?: string[];
}

export interface ImsProfile {
  userId: string;
  userType: string;
  description: string;
  owningOrgId: string | null;
  visible: boolean;
  default: boolean;
  status: string;
  hasLowPriority: boolean;
  isSuppressed: boolean;
}

export interface ImsCatalog {
  profiles: ImsProfile[];
  organizations: ImsOrg[];
  profileOrgMap: Record<string, string[]>;
  count: number;
}

export async function fetchImsOrgs(): Promise<ImsCatalog> {
  const empty: ImsCatalog = { profiles: [], organizations: [], profileOrgMap: {}, count: 0 };
  try {
    const token = getAdobeAccessToken();
    const resp = await fetch(actionUrl('fetch-ims-orgs'), {
      method: 'POST',
      headers: authHeaders(token || ''),
      // body: JSON.stringify({ client_id: '' }),
    });
    if (!resp.ok) return empty;
    const body = await resp.json() as ImsCatalog;
    return {
      profiles:      body.profiles      ?? [],
      organizations: body.organizations ?? [],
      profileOrgMap: body.profileOrgMap ?? {},
      count:         body.count         ?? 0,
    };
  } catch (error) {
    console.error('fetchImsOrgs error:', error);
    return empty;
  }
}
